
import Vue from 'vue';
import BaseSlider from '@/components/base/BaseSlider.vue';

// The threshold snaps to this many tokens; finer than that is noise at these balances
const STEP_TOKENS: number = 50;
// Clear space the balance caption keeps from an endpoint label before that label gives way
const SCALE_LABEL_GAP_PX: number = 8;

/**
 * The "when my balance falls below" control: a stepped slider with the account's current balance
 * marked on the track, so "will saving charge me right now?" is answerable by looking at which
 * side of the marker the thumb is on.
 *
 * A slider rather than a free-text field because the only valid values are STEP_TOKENS multiples
 * between one step and what a single top-up buys — the control makes anything else impossible to
 * enter instead of validating it away afterwards.
 *
 * The step is this component's business, so `max` is taken raw and floored here. Values are handed
 * back snapped, and a `value` this component would not have produced (a first-time default, a
 * saved threshold from before a plan change) is corrected on arrival — snapping is an invariant of
 * the control, not something each caller has to remember.
 */
export default Vue.extend({
	name: 'ThresholdSlider',
	props: {
		// Threshold in whole tokens. Always emitted snapped, so a parent can store what it is given.
		value: { type: Number, default: 0 },
		// Ceiling in whole tokens, before flooring to a step. What one top-up buys: a threshold above
		// that would re-trigger the moment the purchase lands.
		max: { type: Number, default: 0 },
		// Current balance in whole tokens, drawn as a marker on the track
		balanceTokens: { type: Number, default: 0 },
		disabled: { type: Boolean, default: false },
	},
	data () {
		return {
			// Scale geometry, measured rather than assumed. How far the track falls short of this
			// element is BaseSlider's business (it reserves the append slot for the number box), so
			// reading it back off the DOM keeps the labels aligned without restating that maths here.
			trackWidth: 0,
			balanceLabelWidth: 0,
			minLabelWidth: 0,
			maxLabelWidth: 0,
			scaleObserver: null as ResizeObserver | null,
		};
	},
	computed: {
		sliderMin (): number {
			return STEP_TOKENS;
		},
		sliderStep (): number {
			return STEP_TOKENS;
		},
		sliderMax (): number {
			const floored: number = Math.floor((Number(this.max) || 0) / STEP_TOKENS) * STEP_TOKENS;
			return Math.max(floored, this.sliderMin);
		},
		/*
		Where the current balance sits on the track, so "will this charge now?" is visible before
		reading anything: thumb to the right of the marker means below-threshold today. Hidden when
		the balance is above the ceiling, since no reachable threshold would trigger.
		 */
		showBalanceMarker (): boolean {
			return this.balanceTokens <= this.sliderMax;
		},
		// Same 0-100 basis as balanceMarkerPct, so the two-tone fill can split exactly at the marker
		thumbPct (): number {
			return this.trackPct(this.value);
		},
		balanceMarkerPct (): number {
			return this.trackPct(this.balanceTokens);
		},
		trackWidthCss (): string {
			return this.trackWidth > 0 ? `${this.trackWidth}px` : '100%';
		},
		/*
		Where the balance caption sits under the track. Centred on the marker while there is room for
		it, but the marker pins to 0% the moment the balance is spent — which is exactly when someone
		is most likely to be on this screen — and a centred caption would hang off the end. At either
		extreme it goes flush instead. Whether an endpoint label then gives way is minLabelCovered and
		maxLabelCovered's call.
		 */
		balanceLabelAnchor (): 'start' | 'center' | 'end' {
			if (!this.showBalanceMarker || !(this.trackWidth > 0) || !(this.balanceLabelWidth > 0)) return 'center';
			const centerPx: number = (this.balanceMarkerPct / 100) * this.trackWidth;
			const halfLabel: number = this.balanceLabelWidth / 2;
			if (centerPx < halfLabel) return 'start';
			if (centerPx > this.trackWidth - halfLabel) return 'end';
			return 'center';
		},
		// The balance caption's left and right edges along the track, in px, wherever its anchor put it
		balanceLabelBox (): { left: number, right: number } | null {
			if (!this.showBalanceMarker || !(this.trackWidth > 0) || !(this.balanceLabelWidth > 0)) return null;
			const width: number = this.balanceLabelWidth;
			if (this.balanceLabelAnchor === 'start') return { left: 0, right: width };
			if (this.balanceLabelAnchor === 'end') return { left: this.trackWidth - width, right: this.trackWidth };
			const centerPx: number = (this.balanceMarkerPct / 100) * this.trackWidth;
			return { left: centerPx - width / 2, right: centerPx + width / 2 };
		},
		/*
		The balance caption beats an endpoint label whenever the two would touch, not only once the
		caption is pushed flush against that end. Centred on a marker near either end, it runs into
		"min" or "max" well before it reaches the end of the track, and the two captions printed over
		each other ("Your balance 8,991" across "10,000 max"). Which side of the marker you are on
		matters more than a bound the slider already will not let you pass.
		 */
		minLabelCovered (): boolean {
			const box = this.balanceLabelBox;
			return !!(box && this.minLabelWidth > 0 && box.left < this.minLabelWidth + SCALE_LABEL_GAP_PX);
		},
		maxLabelCovered (): boolean {
			const box = this.balanceLabelBox;
			return !!(box && this.maxLabelWidth > 0 && box.right > this.trackWidth - this.maxLabelWidth - SCALE_LABEL_GAP_PX);
		},
	},
	watch: {
		/*
		A value we would not have produced gets corrected rather than displayed: a first-time default,
		or a saved threshold the ceiling has since dropped below because the pack changed. Immediate,
		so a seeded value is right on the first render. This cannot loop — snapping a snapped value
		returns it unchanged, since both bounds are themselves multiples of the step.
		 */
		value: {
			immediate: true,
			handler: 'normalizeValue',
		},
		max: 'normalizeValue',
		// The observer covers resizes; the caption's own width changes with its text instead, which
		// nothing resizes, so those two get watched directly.
		balanceTokens: 'remeasureScale',
		showBalanceMarker: 'remeasureScale',
		// The "max" caption's text, and so its width, follows the ceiling
		sliderMax: 'remeasureScale',
	},
	mounted () {
		this.setupScaleObserver();
	},
	beforeDestroy () {
		if (this.scaleObserver) {
			this.scaleObserver.disconnect();
			this.scaleObserver = null;
		}
	},
	methods: {
		// Clamp into range and round to the step, so a typed value or a stale saved value never lands
		// between ticks or above what one top-up buys
		snapTokens (tokens: number): number {
			const snapped: number = Math.round((Number(tokens) || 0) / this.sliderStep) * this.sliderStep;
			return Math.min(Math.max(snapped, this.sliderMin), this.sliderMax);
		},
		trackPct (tokens: number): number {
			const span: number = this.sliderMax - this.sliderMin;
			if (span <= 0) return 0;
			const pct: number = ((tokens - this.sliderMin) / span) * 100;
			return Math.min(Math.max(pct, 0), 100);
		},
		normalizeValue (): void {
			const snapped: number = this.snapTokens(this.value);
			if (snapped !== this.value) this.$emit('input', snapped);
		},
		onSliderInput (tokens: number): void {
			this.$emit('input', this.snapTokens(tokens));
		},
		setupScaleObserver (): void {
			const root = this.$refs.root as HTMLElement;
			if (!root || typeof ResizeObserver === 'undefined') {
				this.measureScale();
				return;
			}
			this.scaleObserver = new ResizeObserver(() => this.measureScale());
			this.scaleObserver.observe(root);
			// The track is the thing the labels line up with, and it can change width while this
			// element does not, so it is watched in its own right rather than inferred from the root.
			const track = root.querySelector('.v-slider__track-container');
			if (track) this.scaleObserver.observe(track);
			this.measureScale();
		},
		measureScale (): void {
			const root = this.$refs.root as HTMLElement;
			// Re-queried every time rather than held, so a re-rendered track never leaves this stale
			const track = root && root.querySelector('.v-slider__track-container');
			const label = this.$refs.balanceLabel as HTMLElement;
			const minLabel = this.$refs.scaleMin as HTMLElement;
			const maxLabel = this.$refs.scaleMax as HTMLElement;
			this.trackWidth = track ? track.getBoundingClientRect().width : 0;
			this.balanceLabelWidth = label ? label.getBoundingClientRect().width : 0;
			this.minLabelWidth = minLabel ? minLabel.getBoundingClientRect().width : 0;
			this.maxLabelWidth = maxLabel ? maxLabel.getBoundingClientRect().width : 0;
		},
		remeasureScale (): void {
			this.$nextTick(this.measureScale);
		},
	},
	components: {
		BaseSlider,
	},
});
